<template>
  <EnhancedDataTable
    ref="dataTable"
    title="Benefits"
    :items="allBenefits || []"
    :headers="headers"
    item-key="Benefit_SK"
    selection-state-name="selectedBenefitSks"
    :loading="allBenefitsLoading ? 'info' : false"
    :initial-items-per-page="20"
    :footer-props="{ itemsPerPageOptions: [-1, 10, 15, 20, 25, 50, 100] }"
    show-select
    select-strategy="page"
    show-crud-actions
    show-reset-filters
    show-unselect-all
    entity-name="Benefit"
    @add="openDialog()"
    @edit="openDialog($event)"
    @delete="handleDelete($event)"
    @merge="openMergeDialog($event)"
    max-height="calc(100vh - 180px)"
  />

  <v-dialog v-model="dialog" max-width="500px" @keydown.esc="close" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md>
          <v-row wrap>
            <v-col cols="12">
              <v-text-field v-model="editedItem.Benefit_Name" label="Benefit Name" autofocus></v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="save" class="pr-5">
          <v-icon start>mdi-content-save-outline</v-icon>Save
        </v-btn>
        <v-btn color="secondary" @click="close" class="pr-4">
          <v-icon start>mdi-close</v-icon>Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <MergeMasterDataDialog
    v-model="mergeDialog"
    :selected-records="selectedRecordsForMerge"
    :entity-name="'Benefits'"
    item-key="Benefit_SK"
    display-field="Benefit_Name"
    :loading="mergeLoading"
    @merge="handleMerge"
  />

  <DeleteConfirmationDialog
    v-model="deleteDialog"
    entity-name="Benefit"
    :item-name="itemToDelete?.Benefit_Name"
    @confirm="confirmDelete"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useQuery, useMutation } from '@vue/apollo-composable'

import EnhancedDataTable from '@/components/EnhancedDataTable.vue'
import MergeMasterDataDialog from '@/components/MergeMasterDataDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'

import ALL_BENEFITS from '@/graphql/Benefit/allBenefits.gql'
import ADD_BENEFIT from '@/graphql/Benefit/addBenefit.gql'
import UPDATE_BENEFIT from '@/graphql/Benefit/updateBenefit.gql'
import REMOVE_BENEFIT from '@/graphql/Benefit/removeBenefit.gql'
import MERGE_BENEFIT from '@/graphql/Benefit/mergeBenefit.gql'

const store = useStore()

const {
  result: allBenefitsResult,
  loading: allBenefitsLoading,
} = useQuery(ALL_BENEFITS)
const allBenefits = computed(() => allBenefitsResult.value?.allBenefits)

const headers = [
  { title: '#', key: 'Benefit_SK', filterType: 'number', align: 'center' },
  { title: 'Name', key: 'Benefit_Name', filterType: 'text', align: 'center' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '120px' }
]

const dataTable = ref(null)
const editedIndex = ref(-1)
const editedItem = ref({})
const formTitle = computed(() => editedIndex.value === -1 ? 'New Benefit' : 'Edit Benefit')
const dialog = ref(false)
const mergeDialog = ref(false)
const mergeLoading = ref(false)
const selectedRecordsForMerge = ref([])
const deleteDialog = ref(false)
const itemToDelete = ref(null)

const addError = (message) => store.commit('addError', message)
const addSuccess = (message) => store.commit('addSuccess', message)

const openDialog = (item = null) => {
  if (item) {
    editedIndex.value = allBenefits.value?.indexOf(item) ?? -1
    editedItem.value = { ...item }
  } else {
    editedIndex.value = -1
    editedItem.value = {}
  }
  dialog.value = true
}

const close = () => {
  dialog.value = false
  setTimeout(() => {
    editedItem.value = {}
    editedIndex.value = -1
  }, 300)
}

const { mutate: addBenefit } = useMutation(ADD_BENEFIT,
  () => ({
    update: (cache, { data: { addBenefit } }) => {
      const { allBenefits } = cache.readQuery({ query: ALL_BENEFITS })
      const newData = { allBenefits: [...allBenefits, addBenefit] }
      cache.writeQuery({ query: ALL_BENEFITS, data: newData })
    }
  })
)

const { mutate: updateBenefit } = useMutation(UPDATE_BENEFIT)

const save = () => {
  if (editedIndex.value > -1) {
    updateBenefit({
      UpdateBenefitInput: {
        Benefit_SK: editedItem.value.Benefit_SK,
        Benefit_Name: editedItem.value.Benefit_Name
      }
    })
      .then(() => close())
      .catch((error) => addError(error.message))
  } else {
    addBenefit({
      AddBenefitInput: {
        Benefit_Name: editedItem.value.Benefit_Name
      }
    })
      .then(() => close())
      .catch((error) => addError(error.message))
  }
}

const { mutate: removeBenefit } = useMutation(REMOVE_BENEFIT)

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = () => {
  const item = itemToDelete.value
  if (!item) return

  removeBenefit({
    Benefit_SK: item.Benefit_SK
  }, {
    update: (cache, { data: { removeBenefit } }) => {
      const { allBenefits } = cache.readQuery({ query: ALL_BENEFITS })
      const newData = { allBenefits: allBenefits.filter((b) => b.Benefit_SK !== removeBenefit) }
      cache.writeQuery({ query: ALL_BENEFITS, data: newData })
      // Remove deleted item from selection
      dataTable.value?.removeFromSelection(item.Benefit_SK)
    }
  })
    .catch((error) => addError(error.message))
    .finally(() => {
      itemToDelete.value = null
    })
}

const openMergeDialog = (records) => {
  selectedRecordsForMerge.value = records
  mergeDialog.value = true
}

const { mutate: mergeBenefit } = useMutation(MERGE_BENEFIT)

const handleMerge = (mergeData) => {
  mergeLoading.value = true
  mergeBenefit({
    MergeBenefitInput: {
      survivor_Benefit_SK: mergeData.survivor,
      duplicate_Benefit_SKs: mergeData.duplicates
    }
  }, {
    update: (cache) => {
      const { allBenefits } = cache.readQuery({ query: ALL_BENEFITS })
      const newData = {
        allBenefits: allBenefits.filter(benefit => !mergeData.duplicates.includes(benefit.Benefit_SK))
      }
      cache.writeQuery({ query: ALL_BENEFITS, data: newData })
    }
  })
    .then(() => {
      // Remove merged duplicates from selection
      dataTable.value?.removeFromSelection(mergeData.duplicates)
      mergeDialog.value = false
      addSuccess(`Successfully merged ${mergeData.duplicates.length + 1} benefits`)
    })
    .catch((error) => addError(error.message))
    .finally(() => {
      mergeLoading.value = false
    })
}
</script>
